import React from 'react';
import { Box } from '@mui/material';

/**
 * Shared tab panel. Keep this at module scope (or import here) — never define inside a
 * parent component, or React remounts children on every parent re-render.
 *
 * @param {boolean} [unmountInactive=false] When true, inactive panels do not keep children mounted.
 * @param {number} [padding=3] MUI Box padding for the panel body (layout uses 0).
 * @param {boolean} [fillHeight=true] When padding is 0, stretch the body with flex: 1.
 *   Portal pages pass false so content can grow taller than the viewport.
 */
export default function TabPanel({
    children,
    value,
    index,
    unmountInactive = false,
    padding = 3,
    fillHeight = true,
    style,
    ...other
}) {
    const active = value === index;
    let bodySx;
    let panelStyle = style;
    if (padding === 0) {
        // Flex column so the body can use flex: 1 instead of height/minHeight percentages.
        panelStyle = {
            ...style,
            display: active ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
        };
        // fillHeight: fill a fixed-height marketing panel (minHeight 0 allows parent overflow).
        // !fillHeight: grow with portal content while still filling the panel's min-height.
        bodySx = fillHeight ? { flex: 1, minHeight: 0 } : { flex: 1 };
    }
    return (
        <Box
            component="div"
            role="tabpanel"
            hidden={!active}
            id={`tabpanel-${index}`}
            {...other}
            style={panelStyle}
        >
            <Box p={padding} sx={bodySx}>
                {!unmountInactive || active ? children : null}
            </Box>
        </Box>
    );
}
